import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type { NormalizedSchema } from 'normalizr';
import { normalize } from 'normalizr';
import { ApiRequestError, unwrapFetch2 } from '~/fetch';
import type {
	IProductHeader,
	IProductLine,
	IProductLinesNormalizeResult,
} from '~/Slices/schema';
import { productLineEntity, productLineId } from '~/Slices/schema';
import type { IAppState } from '~/appstate';
import * as Hub from '../ogm-hub';
import { fetchMultipleLineLevelPrices } from './ProductLinePricesSlice';

export type IProductLinesState = EntityState<IProductLine>;

const productLinesAdapter = createEntityAdapter<IProductLine>({
	selectId: productLineId,
});

export interface IFetchProductLinesParameters {
	blueprintId: number;
	productHeaders: IProductHeader[];
}

export const fetchProductLines = createAsyncThunk<
	NormalizedSchema<IProductLinesNormalizeResult, string>,
	IFetchProductLinesParameters,
	{ state: IAppState; rejectValue: string[] }
>(
	'productLines/fetchProductLines',
	async (
		params: IFetchProductLinesParameters,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const result = await unwrapFetch2(
				Hub.ProductController.GetProductLines(
					params.blueprintId,
					params.productHeaders.map((x) => x.supplierProductNumber)
				)
			);
			const fixedArray = result.productLines.map((productLine) => {
				const phaseOutKey =
					productLine.phaseOutReplacementProductHeader &&
					[
						productLine.phaseOutReplacementProductHeader.corpSupplierCode,
						productLine.phaseOutReplacementProductHeader.supplierProductNumber,
					].join('|');

				const line = {
					...productLine,
					supplierProductNumber: productLine.product.supplierProductNumber,
					corpSupplierCode: productLine.product.corpSupplierCode,
					phaseOutReplacementProduct: phaseOutKey,
				};

				return line;
			});

			Promise.all([
				dispatch(
					fetchMultipleLineLevelPrices({
						blueprintId: params.blueprintId,
						corpSupplierCode: params.productHeaders.find((x) => x)
							.corpSupplierCode,
						supplierProductNumbers: params.productHeaders.map(
							(x) => x.supplierProductNumber
						),
					})
				),
			]);
			return normalize<any, IProductLinesNormalizeResult>(fixedArray, [
				productLineEntity,
			]);
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			throw err;
		}
	}
);

export const productLinesSlice = createSlice({
	name: 'productLines',
	initialState: productLinesAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchProductLines.fulfilled, (state, action) => {
			productLinesAdapter.upsertMany(
				state,
				action.payload.entities.productLines
			);
		});
	},
});

export function selectProductLinesForSupplierAndProduct(
	state,
	ids: string[]
): IProductLine[] {
	return ids.flatMap((id) => selectProductLineForSupplierAndProduct(state, id));
}

export function selectProductLineById(state, id: string): IProductLine {
	return state.productLines.entities[id];
}

export function selectProductLineForSupplierAndProduct(
	state,
	productId: string
): IProductLine[] {
	if (state.productLines.ids.length > 0) {
		const ids = state.productLines.ids.filter((productLine) => {
			return (
				productLine.split('|')[0] === productId.split('|')[0] &&
				productLine.split('|')[2] === productId.split('|')[1]
			);
		});
		return ids.map((id) => selectProductLineById(state, id));
	}
	return [];
}

export function createProductLineId(
	corpSupplierCode: string,
	distributionCenterDssiCode: string,
	supplierProductNumber: string
) {
	return [
		corpSupplierCode,
		distributionCenterDssiCode,
		supplierProductNumber,
	].join('|');
}
